import React, {useEffect, useState} from 'react';
import {Transition} from "@headlessui/react";
import {ICategory} from "../../model/ICategory";
import {TransactionType} from "../../model/TransactionType";
import {categoryApi} from "../../api/CategoriesService";
import CategoryPicker from "../ui/transaction/CategoryPicker";
import {classNames} from "../../utils/common";
import {getApiErrorMessage, ROOT_CATEGORY_OPTION} from "./categoryTreeUtils";

interface NewCategoryFormProps {
    show: boolean;
    trType: TransactionType;
    categories: ICategory[];
    onClose: () => void;
    onCreated: (categoryId: number) => void;
}

const NewCategoryForm: React.FunctionComponent<NewCategoryFormProps> = ({
    show,
    trType,
    categories,
    onClose,
    onCreated,
}) => {
    const [name, setName] = useState('');
    const [parent, setParent] = useState<ICategory>(ROOT_CATEGORY_OPTION);
    const [error, setError] = useState('');
    const [createCategory, {isLoading}] = categoryApi.useCreateCategoryMutation();

    const parentOptions = [ROOT_CATEGORY_OPTION, ...categories];

    useEffect(() => {
        if (!show) {
            return;
        }
        setName('');
        setError('');
        setParent(ROOT_CATEGORY_OPTION);
    }, [show]);

    async function handleAdd() {
        const trimmedName = name.trim();
        if (!trimmedName) {
            setError('Name is required.');
            return;
        }
        setError('');
        try {
            const created = await createCategory({
                trType,
                name: trimmedName,
                root_category: parent.id === ROOT_CATEGORY_OPTION.id ? null : parent.id,
            }).unwrap();
            onCreated(created.id);
            onClose();
        } catch (err) {
            setError(getApiErrorMessage(err, 'Could not create category.'));
        }
    }

    return (
        <Transition
            show={show}
            enter="ease-in-out duration-300 transform"
            enterFrom="-translate-y-full opacity-0"
            enterTo="translate-y-0 opacity-100"
            leave="ease-in-out duration-300 transform"
            leaveFrom="translate-y-0 opacity-100"
            leaveTo="-translate-y-full opacity-0"
            className="relative z-50"
        >
            <div className="mb-2 border-b border-gray-200 relative z-50 py-1">
                <div className="relative z-50 flex items-end justify-center flex-wrap mx-3 px-1 focus-within:z-[60]">
                    <div className="mt-2 mx-3">
                        <label className="ml-1 block text-xs font-medium text-gray-700">Name</label>
                        <div className="mt-0.5 h-7 relative rounded-md shadow-sm">
                            <input
                                value={name}
                                onChange={(event) => setName(event.target.value)}
                                type="text"
                                className="h-7 w-44 px-2 text-xs focus:ring-indigo-500 focus:border-indigo-500 block border-gray-300 rounded-md"
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') {
                                        event.preventDefault();
                                        handleAdd();
                                    }
                                }}
                            />
                        </div>
                    </div>
                    <CategoryPicker
                        label="Parent"
                        category={parent}
                        setCategory={setParent}
                        categories={parentOptions}
                        smallerSize
                        className="mt-2 mx-3"
                    />
                    <div className="flex items-center ml-3 mb-0.5 gap-2">
                        <button
                            type="button"
                            className="group relative flex justify-center py-1 px-3 border border-transparent
                                       text-xs font-medium rounded-md bg-gray-200 text-red-500 hover:text-red-800"
                            onClick={onClose}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="group relative flex justify-center py-1 px-3 border border-transparent
                                       text-xs font-medium rounded-md bg-green-100 text-green-800 hover:bg-green-200 focus:outline-none"
                            disabled={isLoading}
                            onClick={handleAdd}
                        >
                            Add
                        </button>
                    </div>
                </div>
                {error && (
                    <p className={classNames("mx-4 mt-1 mb-1 text-xs text-red-700")}>{error}</p>
                )}
            </div>
        </Transition>
    );
};

export default NewCategoryForm;
